<template>
  <div>
    <v-alert
      v-if="erro"
      type="error"
      dense
      outlined
      role="alert"
    >
      {{ erro }}
    </v-alert>
    <v-data-table
      :headers="headers"
      :items="items"
      :loading="loading"
      :header-props="{ sortByText: 'Ordenar por' }"
      :footer-props="{ itemsPerPageText: 'Itens por página' }"
      loading-text="Carregando importações..."
      no-data-text="Nenhuma importação de justificativas solicitada"
      mobile-breakpoint="900"
    >
      <template v-slot:[`item.created`]="{ item }">
        {{ formatarData(item.created) }}
      </template>
      <template v-slot:[`item.processado_em`]="{ item }">
        {{ formatarData(item.processado_em) }}
      </template>
      <template v-slot:[`item.status`]="{ item }">
        <general-status :status="item.status" />
      </template>
      <template v-slot:[`item.actions`]="{ item }">
        <div class="d-flex">
          <v-btn
            v-for="tipo in ['original', 'resultado']"
            :key="tipo"
            icon
            small
            :disabled="!disponivel(item, tipo) || !!downloads[`${item.id}-${tipo}`]"
            :loading="!!downloads[`${item.id}-${tipo}`]"
            :aria-label="`Baixar ${tipo} da importação ${item.id}`"
            :title="tipo === 'original' ? 'Baixar arquivo original' : 'Baixar resultado do processamento'"
            @click="baixar(item, tipo)"
          >
            <v-icon small>
              {{ tipo === 'original' ? 'mdi-file-download-outline' : 'mdi-download' }}
            </v-icon>
          </v-btn>
          <v-btn
            icon
            small
            :aria-label="`Detalhes da importação ${item.id}`"
            title="Detalhes do processamento"
            @click="selecionado = item.id"
          >
            <v-icon small>
              mdi-file-find-outline
            </v-icon>
          </v-btn>
        </div>
      </template>
      <template v-slot:[`footer.page-text`]="props">
        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <v-icon
              color="primary"
              v-bind="attrs"
              v-on="on"
              @click="carregar"
            >
              mdi-refresh
            </v-icon>
          </template>
          <span>Clique aqui para recarregar as importações</span>
        </v-tooltip>
        <span class="ml-4">{{ props.pageStart }}-{{ props.pageStop }} de {{ props.itemsLength }}</span>
      </template>
    </v-data-table>
    <general-dialog-detalhes
      :title="detalhes ? `Detalhes da Importação de Justificativas #${detalhes.id}` : ''"
      :show-dialog="!!detalhes"
      max-width="1200px"
      @changeValue="!$event && (selecionado = null)"
    >
      <template slot="body">
        <v-data-table
          :headers="resumoHeaders"
          :items="resumoItens"
          hide-default-footer
          disable-pagination
          class="elevation-1 style-table-dialog-detalhes"
          mobile-breakpoint="10"
        />
      </template>
    </general-dialog-detalhes>
  </div>
</template>

<script>
import dayjs from 'dayjs';
import AieJustificativasService from '@/services/AieJustificativasService';

export default {
  name: 'AieImportacoes',
  components: {
    GeneralStatus: () => import('@/components/general/GeneralStatus.vue'),
    GeneralDialogDetalhes: () => import('@/components/general/GeneralDialogDetalhes')
  },
  data() {
    return {
      items: [], loading: false, erro: '', downloads: {}, selecionado: null, timer: null, ativo: true,
      resumoHeaders: [
        { text: 'Resumo', value: 'parametro', sortable: false },
        { text: 'Valor', value: 'valor', sortable: false }
      ],
      headers: [
        { text: '#', value: 'id' },
        { text: 'Arquivo', value: 'nome_arquivo' },
        { text: 'Usuário', value: 'usuario.nome' },
        { text: 'Enviado em', value: 'created' },
        { text: 'Concluído em', value: 'processado_em' },
        { text: 'Status', value: 'status' },
        { text: 'PEPs atualizados', value: 'qtd_atualizadas' },
        { text: 'Erros', value: 'qtd_erros' },
        { text: 'Ações', value: 'actions', sortable: false }
      ]
    };
  },
  computed: {
    detalhes() { return this.items.find((item) => item.id === this.selecionado); },

    resumoItens() {
      const item = this.detalhes;
      if (!item) return [];
      return [
        { parametro: 'ID', valor: item.id },
        { parametro: 'ARQUIVO', valor: item.nome_arquivo || '-' },
        { parametro: 'USUARIO', valor: item.usuario ? item.usuario.nome : '-' },
        { parametro: 'STATUS', valor: item.status },
        { parametro: 'ENVIADO EM', valor: this.formatarData(item.created) },
        { parametro: 'CONCLUIDO EM', valor: this.formatarData(item.processado_em) },
        { parametro: 'LINHAS PREENCHIDAS', valor: item.qtd_linhas },
        { parametro: 'PEPS ATUALIZADOS', valor: item.qtd_atualizadas },
        { parametro: 'ERROS', valor: item.qtd_erros },
        { parametro: 'RESUMO', valor: item.resumo || '-' }
      ];
    }
  },
  mounted() { this.carregar(); },
  beforeDestroy() {
    this.ativo = false;
    clearTimeout(this.timer);
  },
  methods: {
    formatarData(data) { return data ? dayjs(data).format('DD/MM/YYYY HH:mm') : '-'; },
    disponivel(item, tipo) {
      return tipo === 'original' ? item.original_disponivel : item.resultado_disponivel && ['SUCESSO', 'FALHA'].includes(item.status);
    },
    async carregar() {
      if (this.loading || !this.ativo) return;
      clearTimeout(this.timer);
      this.loading = true;
      try {
        const response = await AieJustificativasService.historico();
        this.items = response.data.data || [];
        this.erro = '';
      } catch (error) {
        this.erro = 'Não foi possível atualizar o histórico. Tentaremos novamente em alguns segundos.';
      } finally {
        this.loading = false;
        if (this.ativo) this.timer = setTimeout(() => this.carregar(), 15000);
      }
    },
    async baixar(item, tipo) {
      const chave = `${item.id}-${tipo}`;
      if (!this.disponivel(item, tipo) || this.downloads[chave]) return;
      this.$set(this.downloads, chave, true);
      try {
        const response = await AieJustificativasService.baixar(item.id, tipo);
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.download = tipo === 'original' ? item.nome_arquivo : `resultado_importacao_iea_${item.id}.xlsx`;
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      } catch (error) {
        this.$toast.error('Não foi possível baixar o arquivo. Atualize o histórico e tente novamente.', '', { position: 'topRight' });
      } finally {
        this.$delete(this.downloads, chave);
      }
    }
  }
};
</script>
